import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// API Configuration
const API_CONFIG = {
  BASE_URL: 'https://fftestservice.sarsetech.com/',
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3', // Replace with actual key
};

// API Endpoints
const ENDPOINTS = {
  REVIEW_LIST: 'user/listReviewsProducts',
  REVIEW_ADD: 'user/addReview',
  REVIEW_UPDATE: 'user/updateReviewStatus',
  REVIEW_DELETE: 'admin/deleteReview',
  REVIEW_COUNT: 'admin/reviewCount',
  REVIEW_DETAIL: 'admin/reviewDetail',
};

// Types
export interface Review {
  id: number;
  review_id?: number;
  rating: number;
  comment: string;
  created_date: string;
  customer_name: string;
  product_id: number;
  seller_id: number | null;
  status: number; // 0-pending, 1-approved
  product_name?: string;
  user_id?: number;
}

export interface ApiResponse<T> {
  error: boolean;
  message: string;
  data?: T;
  result?: T;
  count?: number;
}

export interface ReviewListParams {
  page?: number;
  limit?: number;
  search_str?: string;
  status?: number;
  product_id?: number;
}

export interface ReviewCountParams {
  search_str?: string;
  status?: number;
  product_id?: number;
}

export interface ReviewDetailParams {
  review_id: number;
}

export interface ReviewDeleteParams {
  review_id: number;
}

export interface AddReviewRequest {
  product_id: number;
  seller_id: number | null;
  rating: number;
  comment: string;
  type?: number;
  review_id?: number;
}

export interface UpdateReviewRequest {
  review_id: number;
  status: number;
}

export interface ReviewFormData {
  product_id: number;
  seller_id: number | null;
  rating: number;
  comment: string;
  status: number;
}

export interface FormErrors {
  [key: string]: string;
}

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// Create a function to generate cache keys consistently
const generateCacheKey = (params: ReviewListParams | ReviewCountParams) => {
  const key = JSON.stringify({
    search_str: params.search_str?.trim() || '',
    status: params.status,
    product_id: params.product_id,
  });
  return key;
};

// Create the API
export const reviewApi = createApi({
  reducerPath: 'reviewApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      headers.set('authkey', API_CONFIG.AUTH_KEY);
      headers.set('Content-Type', 'application/json');
      
      return headers;
    },
  }),
  tagTypes: ['Review', 'ReviewCount', 'ReviewList'],
  endpoints: (build) => ({
    // 1. List Reviews
    listReviews: build.query<ApiResponse<Review[]>, ReviewListParams>({
      query: ({ page = 1, limit = 10, search_str = '', status, product_id } = {}) => ({
        url: ENDPOINTS.REVIEW_LIST,
        method: 'POST',
        body: JSON.stringify({
          start: (page - 1) * limit,
          limit,
          ...(search_str?.trim() && { search_str: search_str.trim() }),
          ...(status !== undefined && { status }),
          ...(product_id !== undefined && { product_id }), 
        }),
      }),
      providesTags: (result, error, arg) => {
        const tags: any[] = ['ReviewList'];
        
        if (result?.result) {
          // Add specific tags for each review
          result.result.forEach((review) => {
            tags.push({ type: 'Review', id: review.id });
            tags.push({ type: 'Review', id: review.review_id });
          });
        }
        
        // Add tag based on query parameters
        const cacheKey = generateCacheKey(arg);
        tags.push({ type: 'ReviewList', id: cacheKey });
        
        return tags;
      },
      transformResponse: (response: any) => {
        console.log('List Reviews Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.result || response.data || [],
          count: response.count || response.total || 0,
        };
      },
    }),

    // 2. Add Review
    addReview: build.mutation<ApiResponse<any>, AddReviewRequest>({
      query: (data) => ({
        url: ENDPOINTS.REVIEW_ADD,
        method: 'POST',
        body: JSON.stringify(data),
      }),
      // Invalidate all review-related caches
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          console.log('Add review success:', result);
          
          // Force invalidate all tags to ensure fresh data
          dispatch(reviewApi.util.invalidateTags(['Review', 'ReviewCount', 'ReviewList']));
        } catch (error) {
          console.error('Add review failed:', error);
        }
      },
    }),

    // 3. Update Review Status
    updateReview: build.mutation<ApiResponse<any>, UpdateReviewRequest>({
      query: (data) => ({
        url: ENDPOINTS.REVIEW_UPDATE,
        method: 'POST',
        body: JSON.stringify(data),
      }),
      // Invalidate all review-related caches
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList'],
      async onQueryStarted({ review_id, status }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          console.log('Update review success:', result);
          
          // Force invalidate all tags to ensure fresh data
          dispatch(reviewApi.util.invalidateTags(['Review', 'ReviewCount', 'ReviewList']));
        } catch (error) {
          console.error('Update review failed:', error);
        }
      },
    }),

    // 4. Delete Review
    deleteReview: build.mutation<ApiResponse<any>, ReviewDeleteParams>({
      query: ({ review_id }) => ({
        url: ENDPOINTS.REVIEW_DELETE,
        method: 'POST',
        body: JSON.stringify({ review_id }),
      }),
      // Invalidate all review-related caches
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList'],
      async onQueryStarted({ review_id }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          console.log('Delete review success:', result);
          
          // Force invalidate all tags to ensure fresh data
          dispatch(reviewApi.util.invalidateTags(['Review', 'ReviewCount', 'ReviewList']));
        } catch (error) {
          console.error('Delete review failed:', error);
        }
      },
    }),

    // 5. Get Review Count
    getReviewCount: build.query<ApiResponse<{ count: number }>, ReviewCountParams>({
      query: ({ search_str = '', status, product_id } = {}) => ({
        url: ENDPOINTS.REVIEW_COUNT,
        method: 'POST',
        body: JSON.stringify({
          ...(search_str?.trim() && { search_str: search_str.trim() }),
          ...(status !== undefined && { status }),
          ...(product_id !== undefined && { product_id }),
        }),
      }),
      providesTags: (result, error, arg) => {
        const cacheKey = generateCacheKey(arg);
        return ['ReviewCount', { type: 'ReviewCount', id: cacheKey }];
      },
      transformResponse: (response: any) => {
        console.log('Review Count Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          count: response.count || response.result?.count || 0,
        };
      },
    }),

    // 6. Get Pending Review Count
    getPendingReviewCount: build.query<ApiResponse<{ count: number }>, void>({
      query: () => ({
        url: ENDPOINTS.REVIEW_COUNT,
        method: 'POST',
        body: JSON.stringify({ status: 0 }), // Status 0 for pending
      }),
      providesTags: ['ReviewCount', { type: 'ReviewCount', id: 'pending' }],
      transformResponse: (response: any) => {
        console.log('Pending Review Count Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          count: response.count || response.result?.count || 0,
        };
      },
    }),

    // 7. Get Review Details
    getReviewDetail: build.query<ApiResponse<Review>, ReviewDetailParams>({
      query: ({ review_id }) => ({
        url: ENDPOINTS.REVIEW_DETAIL,
        method: 'POST',
        body: JSON.stringify({ review_id }),
      }),
      providesTags: (result, error, { review_id }) => [
        { type: 'Review', id: review_id },
        'Review'
      ],
      transformResponse: (response: any) => {
        console.log('Review Details Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.result || response.data,
        };
      },
    }),
  }),
});

export const {
  useListReviewsQuery,
  useAddReviewMutation,
  useUpdateReviewMutation,
  useDeleteReviewMutation,
  useGetReviewCountQuery,
  useGetPendingReviewCountQuery,
  useGetReviewDetailQuery,
} = reviewApi;